import { useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Eye, Film, Compass, Target, BookMarked, ClipboardList, ArrowRight } from "lucide-react";
import { t } from "@/lib/i18n";

export const Md = ({ children }) => <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>;

export const LENS_ROLES = [
  ["ops", "Plant and operations leaders"],
  ["eng", "Engineers"],
  ["it", "OT/IT and data leads"],
  ["ci", "Digital and CI managers"],
  ["exec", "Executives"],
];

export const PauseReveal = ({ b, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`pause-${id}`} className="my-6 rounded-xl border-2 border-dashed border-brand-action/60 bg-brand-action/5 p-5">
      <div className="overline-label text-brand-action">{t("Think first")}</div>
      <p className="mt-2 font-semibold">{b.question}</p>
      {open ? <div data-testid={`pause-answer-${id}`} className="reader simple-reader mt-3 border-t border-border pt-3"><Md>{b.answer}</Md></div>
        : <button type="button" data-testid={`pause-reveal-${id}`} onClick={() => setOpen(true)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-action px-4 py-2 text-sm font-semibold text-brand-action-fg transition-opacity hover:opacity-90"><Eye className="h-4 w-4" />{t("Show the answer")}</button>}
    </div>
  );
};

export const LessonIntro = ({ lesson }) => (
  <div data-testid="lesson-intro" className="mt-6 space-y-4">
    {lesson.thread && (
      <div data-testid="lesson-running-case" className="rounded-r-lg border-l-4 border-brand-data bg-brand-data/5 p-4">
        <div className="flex items-center gap-2 overline-label text-brand-data"><Compass className="h-4 w-4" />{t("Running case")}</div>
        <p className="mt-1.5">{lesson.thread}</p>
      </div>
    )}
    {lesson.outcomes?.length > 0 && (
      <div data-testid="lesson-outcomes" className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 overline-label"><Target className="h-4 w-4" />{t("You will be able to")}</div>
        <ul className="mt-2 list-disc space-y-1 pl-5">{lesson.outcomes.map((o) => <li key={o}>{o}</li>)}</ul>
      </div>
    )}
    {lesson.terms?.length > 0 && (
      <details data-testid="lesson-terms" className="rounded-lg border border-border bg-card p-4">
        <summary className="flex cursor-pointer items-center gap-2 font-semibold"><BookMarked className="h-4 w-4 text-brand-primary" />{t("Key terms")} ({lesson.terms.length})</summary>
        <dl className="mt-3 space-y-2 text-sm">{lesson.terms.map((x) => <div key={x.term}><dt className="font-semibold">{x.term}</dt><dd className="text-muted-foreground">{x.definition}</dd></div>)}</dl>
      </details>
    )}
  </div>
);

export const LensBox = ({ lens }) => {
  const [role, setRoleState] = useState(() => localStorage.getItem("cias_lens_role") || "");
  const setRole = (r) => { setRoleState(r); localStorage.setItem("cias_lens_role", r); };
  if (!lens || !Object.keys(lens).length) return null;
  return (
    <section data-testid="lesson-lens" className="rounded-lg border border-border bg-card p-5 sm:p-6">
      <div className="font-heading text-lg font-bold">{t("Through your lens")}</div>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={t("Your role")}>
        {LENS_ROLES.map(([k, label]) => (
          <button key={k} type="button" data-testid={`lens-role-${k}`} aria-pressed={role === k} onClick={() => setRole(role === k ? "" : k)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${role === k ? "border-brand-primary bg-brand-primary text-brand-primary-fg" : "border-border hover:border-brand-primary"}`}>{t(label)}</button>
        ))}
      </div>
      <ul className="mt-4 space-y-2">
        {LENS_ROLES.filter(([k]) => lens[k]).map(([k, label]) => (
          <li key={k} data-testid={`lens-line-${k}`} className={`rounded-md p-3 text-sm transition-colors ${role === k ? "border-l-4 border-brand-primary bg-brand-primary/10 font-medium" : role ? "text-muted-foreground" : ""}`}>
            <span className="font-semibold">{t(label)} · </span>{lens[k]}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const ProjectBox = ({ project, next }) => (
  <>
    {project && (
      <section data-testid="lesson-project" className="rounded-r-lg border-l-4 border-brand-accent bg-brand-accent/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 font-heading text-lg font-bold"><ClipboardList className="h-5 w-5 text-brand-accent" />{t("Your plant project")}</div>
        <div className="reader simple-reader mt-2"><Md>{project}</Md></div>
        <Link to="/project" data-testid="lesson-project-link" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-primary hover:underline">{t("See your whole project")}<ArrowRight className="h-4 w-4" /></Link>
      </section>
    )}
    {next && <p data-testid="lesson-next-teaser" className="border-t border-border pt-4 italic text-muted-foreground"><span className="font-semibold not-italic text-foreground">{t("Next")} · </span>{next}</p>}
  </>
);

export const VideoComing = ({ s, admin, onText }) => (
  <div data-testid={`lesson-video-coming-${s.slug}`} className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-xl bg-neutral-900 p-6 text-center text-white">
    <Film className="h-10 w-10 text-white/60" />
    <div className="font-heading text-xl font-bold">{t("Video coming")}</div>
    {s.onscreen?.map((o) => <div key={o} className="rounded bg-white/10 px-3 py-1 font-mono text-sm">{o}</div>)}
    <button type="button" data-testid="lesson-switch-to-text" onClick={onText} className="mt-2 rounded-full border border-white/40 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10">{t("Switch to Text mode")}</button>
    {admin && s.visual?.length > 0 && <p data-testid="lesson-storyboard" className="max-w-xl text-xs text-white/50">{t("Storyboard (admins only)")}: {s.visual.join(" ")}</p>}
  </div>
);
